import { Country, ProgramLocation } from "../types";
import { detectUserLocation } from "./locationDetection";

/** USD Configuration default when no country package (or detection) is available. */
export const DEFAULT_BILLING_COUNTRY_CODE = "US";

type CountryLookup = Pick<Country, "name" | "iso2">;
type LocationCountrySource = Pick<ProgramLocation, "country" | "isActive">;

/**
 * Map a stored location country (ISO2 or display name) onto a 2-letter code.
 * Locations persist the country *name* ("Australia"), while Stripe billing
 * APIs require ISO2 ("AU").
 * @param {string|undefined} locationCountry - Stored location country name or ISO2.
 * @param {Array<{name?: string, iso2?: string}>} countries - Server country list used for name→ISO2 mapping.
 * @returns {string|undefined} Uppercase ISO2, or undefined when it cannot be resolved.
 */
export const resolveCountryCodeFromLocationCountry = (
  locationCountry: string | undefined,
  countries: CountryLookup[],
): string | undefined => {
  const trimmed = locationCountry?.trim();
  if (!trimmed) {
    return undefined;
  }

  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  const matched = countries.find(
    (country) =>
      country.iso2?.trim().toLowerCase() === trimmed.toLowerCase() ||
      country.name?.trim().toLowerCase() === trimmed.toLowerCase(),
  );

  const iso2 = matched?.iso2?.trim();
  return iso2 ? iso2.toUpperCase() : undefined;
};

/**
 * Prefer an active program location's country so checkout matches where the
 * business actually operates, not where the user happens to be browsing from.
 * @param {Array<{country?: string, isActive?: boolean}>|undefined} locations - Program locations.
 * @param {Array<{name?: string, iso2?: string}>|undefined} countries - Server country list.
 * @returns {string|undefined} Uppercase ISO2 from an existing location, if any.
 */
export const resolveCountryCodeFromLocations = (
  locations: LocationCountrySource[] | undefined,
  countries: CountryLookup[] | undefined,
): string | undefined => {
  if (!locations?.length) {
    return undefined;
  }

  const countryList = countries ?? [];
  const activeLocations = locations.filter(
    (location) => location.isActive !== false,
  );
  const source = activeLocations.length > 0 ? activeLocations : locations;

  for (const location of source) {
    const countryCode = resolveCountryCodeFromLocationCountry(
      location.country,
      countryList,
    );
    if (countryCode) {
      return countryCode;
    }
  }

  return undefined;
};

/**
 * Country for Stripe billing: a single location's country when there is
 * exactly one location; otherwise IP detection (several sites can span
 * countries). USD default if detection fails.
 * @param {Array<{country?: string, isActive?: boolean}>} [locations] - Program locations.
 * @param {Array<{name?: string, iso2?: string}>} [countries] - Server country list.
 * @returns {Promise<string>} Uppercase ISO2 country code for Stripe billing APIs.
 */
export const resolveBillingCountryCode = async (
  locations?: LocationCountrySource[],
  countries?: CountryLookup[],
): Promise<string> => {
  const locationCount = locations?.length ?? 0;
  if (locationCount === 1) {
    const fromLocations = resolveCountryCodeFromLocations(locations, countries);
    if (fromLocations) {
      return fromLocations;
    }
  }

  try {
    const detected = await detectUserLocation();
    const detectedCode = detected?.country_code?.trim();
    if (detectedCode && /^[A-Za-z]{2}$/.test(detectedCode)) {
      return detectedCode.toUpperCase();
    }
  } catch {
    // Detection is best-effort; USD default still produces a valid checkout.
  }

  return DEFAULT_BILLING_COUNTRY_CODE;
};

/**
 * Format a preview-cost amount (already in display / major units) for the
 * given ISO currency. JPY stays whole yen; AUD/USD/PHP keep two decimals.
 * @param {number} amount - Display/major-unit amount from the billing API.
 * @param {string} [currencyCode] - ISO 4217 currency code (defaults to USD).
 * @returns {string} Locale-formatted currency string.
 */
export const formatBillingAmount = (
  amount: number,
  currencyCode?: string,
): string => {
  const code = currencyCode?.trim().toUpperCase() || "USD";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    return `${code} ${amount}`;
  }
};
